import { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';
import {
  LayoutDashboard, Package, Boxes, Clock, Truck, Users, Film, Plus, Pencil, Trash2, Upload, X, DollarSign, AlertTriangle, Layers, Instagram,
} from 'lucide-react';
import { api, resolveImg, money } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const TABS = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard },
  { key: 'products', label: 'Products', icon: Package },
  { key: 'inventory', label: 'Inventory', icon: Boxes },
  { key: 'presale', label: 'Presale', icon: Clock },
  { key: 'orders', label: 'Orders', icon: Truck },
  { key: 'users', label: 'Users', icon: Users },
  { key: 'bundles', label: 'Sets', icon: Layers },
  { key: 'videos', label: 'Films', icon: Film },
];

const CATEGORIES = ['Earrings', 'Rings', 'Cuffs', 'Necklaces', 'Other'];

const emptyProduct = {
  name: '', category: 'Earrings', description: '', price: 0, currency: 'usd', images: [], video_url: '',
  youtube_id: '', stock: 0, low_stock_threshold: 5, is_presale: false, presale_dispatch_date: '', presale_limit: null, featured: false, active: true,
};

function UploadField({ label, accept, onUploaded }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUploaded(data.url);
      toast.success('Uploaded');
    } catch { toast.error('Upload failed'); }
    setBusy(false);
  };
  return (
    <>
      <button type="button" onClick={() => ref.current.click()} data-testid="admin-upload-button"
        className="flex items-center gap-2 border border-border px-4 py-2 text-xs hover:border-gold">
        <Upload size={14} /> {busy ? 'Uploading…' : label}
      </button>
      <input ref={ref} type="file" accept={accept} onChange={upload} className="hidden" />
    </>
  );
}

function ProductForm({ product, onClose, onSaved }) {
  const [form, setForm] = useState(product ? { ...emptyProduct, ...product } : emptyProduct);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    const payload = {
      ...form, price: parseFloat(form.price) || 0, stock: parseInt(form.stock) || 0,
      low_stock_threshold: parseInt(form.low_stock_threshold) || 5,
      presale_limit: form.presale_limit ? parseInt(form.presale_limit) : null,
      presale_dispatch_date: form.presale_dispatch_date || null,
      video_url: form.video_url || null, youtube_id: form.youtube_id || null,
    };
    try {
      if (product) await api.put(`/products/${product.id}`, payload);
      else await api.post('/products', payload);
      toast.success(product ? 'Product updated' : 'Product created');
      onSaved(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="product-form-dialog" className="bg-surface border-border max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{product ? 'Edit Product' : 'New Product'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          <div className="sm:col-span-2"><Label className="text-xs">Name</Label><Input data-testid="product-name-input" value={form.name} onChange={(e) => set('name', e.target.value)} className="mt-1 bg-background border-border" /></div>
          <div><Label className="text-xs">Category</Label>
            <Select value={form.category} onValueChange={(v) => set('category', v)}>
              <SelectTrigger className="mt-1 bg-background border-border"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-surface-elevated border-border">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Price (USD)</Label><Input data-testid="product-price-input" type="number" value={form.price} onChange={(e) => set('price', e.target.value)} className="mt-1 bg-background border-border" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={(e) => set('description', e.target.value)} className="mt-1 bg-background border-border" /></div>

          <div className="sm:col-span-2">
            <Label className="text-xs">Images</Label>
            <div className="flex flex-wrap gap-3 mt-2 items-center">
              {form.images.map((img, i) => (
                <div key={i} className="relative w-16 h-20">
                  <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => set('images', form.images.filter((_, x) => x !== i))} className="absolute -top-2 -right-2 bg-destructive text-white rounded-full w-5 h-5 flex items-center justify-center"><X size={11} /></button>
                </div>
              ))}
              <UploadField label="Upload Image" accept="image/*" onUploaded={(url) => set('images', [...form.images, url])} />
            </div>
            <Input placeholder="…or paste image URL and press Enter" className="mt-2 bg-background border-border text-xs"
              onKeyDown={(e) => { if (e.key === 'Enter' && e.target.value) { set('images', [...form.images, e.target.value]); e.target.value = ''; } }} />
          </div>

          <div>
            <Label className="text-xs">Short Video</Label>
            <div className="mt-2 flex items-center gap-2">
              <UploadField label="Upload Video" accept="video/*" onUploaded={(url) => set('video_url', url)} />
              {form.video_url && <button onClick={() => set('video_url', '')} className="text-xs text-destructive">clear</button>}
            </div>
          </div>
          <div><Label className="text-xs">YouTube ID</Label><Input value={form.youtube_id || ''} onChange={(e) => set('youtube_id', e.target.value)} placeholder="dQw4w9WgXcQ" className="mt-1 bg-background border-border" /></div>

          <div><Label className="text-xs">Stock</Label><Input data-testid="product-stock-input" type="number" value={form.stock} onChange={(e) => set('stock', e.target.value)} className="mt-1 bg-background border-border" /></div>
          <div><Label className="text-xs">Low-stock Alert</Label><Input type="number" value={form.low_stock_threshold} onChange={(e) => set('low_stock_threshold', e.target.value)} className="mt-1 bg-background border-border" /></div>

          <div className="flex items-center justify-between border border-border px-4 py-3"><Label className="text-xs">Featured</Label><Switch checked={form.featured} onCheckedChange={(v) => set('featured', v)} /></div>
          <div className="flex items-center justify-between border border-border px-4 py-3"><Label className="text-xs">Presale</Label><Switch data-testid="product-presale-switch" checked={form.is_presale} onCheckedChange={(v) => set('is_presale', v)} /></div>

          {form.is_presale && (
            <>
              <div><Label className="text-xs">Dispatch Date</Label><Input type="date" value={form.presale_dispatch_date || ''} onChange={(e) => set('presale_dispatch_date', e.target.value)} className="mt-1 bg-background border-border" /></div>
              <div><Label className="text-xs">Presale Limit</Label><Input type="number" value={form.presale_limit || ''} onChange={(e) => set('presale_limit', e.target.value)} className="mt-1 bg-background border-border" /></div>
            </>
          )}
        </div>
        <button data-testid="product-save-button" onClick={save} className="mt-4 w-full bg-gold text-primary-foreground py-3 text-xs font-mono uppercase tracking-widest hover:bg-gold-hover">Save Product</button>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [videos, setVideos] = useState([]);
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [videoForm, setVideoForm] = useState({ title: '', video_url: '', youtube_id: '', instagram_url: '', product_id: '' });
  const [bundles, setBundles] = useState([]);
  const [bundleForm, setBundleForm] = useState({ name: '', description: '', price: '', image: '', product_ids: [] });

  const loadProducts = () => api.get('/products').then(({ data }) => setProducts(data));
  const loadAll = () => {
    api.get('/admin/stats').then(({ data }) => setStats(data));
    loadProducts();
    api.get('/admin/orders').then(({ data }) => setOrders(data));
    api.get('/admin/users').then(({ data }) => setUsers(data));
    api.get('/videos').then(({ data }) => setVideos(data));
    api.get('/bundles').then(({ data }) => setBundles(data));
  };
  useEffect(() => { loadAll(); }, []);

  const delProduct = async (id) => { await api.delete(`/products/${id}`); toast.success('Deleted'); loadProducts(); api.get('/admin/stats').then(({ data }) => setStats(data)); };
  const setOrderStatus = async (id, status) => { await api.put(`/admin/orders/${id}/status`, { status }); api.get('/admin/orders').then(({ data }) => setOrders(data)); };
  const setRole = async (id, role) => { await api.put(`/admin/users/${id}/role`, { role }); api.get('/admin/users').then(({ data }) => setUsers(data)); toast.success('Role updated'); };
  const addVideo = async () => {
    if (!videoForm.title || (!videoForm.video_url && !videoForm.youtube_id && !videoForm.instagram_url)) { toast.error('Title and a video source (upload, YouTube or Instagram) required'); return; }
    await api.post('/videos', { ...videoForm, product_id: videoForm.product_id || null });
    setVideoForm({ title: '', video_url: '', youtube_id: '', instagram_url: '', product_id: '' });
    api.get('/videos').then(({ data }) => setVideos(data)); toast.success('Film added');
  };
  const delVideo = async (id) => { await api.delete(`/videos/${id}`); api.get('/videos').then(({ data }) => setVideos(data)); };
  const syncInstagram = async () => {
    try { const { data } = await api.post('/videos/sync-instagram'); toast.success(`Synced ${data.added} reel(s)`); api.get('/videos').then(({ data }) => setVideos(data)); }
    catch (e) { toast.error(e.response?.data?.detail || 'Instagram not connected'); }
  };
  const toggleBundleProduct = (id) => setBundleForm((f) => ({ ...f, product_ids: f.product_ids.includes(id) ? f.product_ids.filter((x) => x !== id) : [...f.product_ids, id] }));
  const saveBundle = async () => {
    if (!bundleForm.name || !bundleForm.price || bundleForm.product_ids.length < 2) { toast.error('Name, price and at least 2 products required'); return; }
    const image = bundleForm.image || products.find((p) => p.id === bundleForm.product_ids[0])?.images?.[0] || null;
    await api.post('/bundles', { ...bundleForm, price: parseFloat(bundleForm.price), image });
    setBundleForm({ name: '', description: '', price: '', image: '', product_ids: [] });
    api.get('/bundles').then(({ data }) => setBundles(data)); toast.success('Set created');
  };
  const delBundle = async (id) => { await api.delete(`/bundles/${id}`); api.get('/bundles').then(({ data }) => setBundles(data)); };

  const lowStock = products.filter((p) => !p.is_presale && p.stock <= p.low_stock_threshold);
  const presaleProducts = products.filter((p) => p.is_presale);

  return (
    <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-12">
      <div className="flex items-center justify-between mb-10">
        <div><p className="eyebrow mb-2">Maison Control</p><h1 className="font-serif text-4xl">Admin Panel</h1></div>
      </div>

      <div className="flex gap-2 mb-10 overflow-x-auto border-b border-border pb-px">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`admin-nav-${t.key}-tab`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-mono uppercase tracking-wider whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t.key ? 'border-gold text-gold' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && stats && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { l: 'Revenue', v: money(stats.revenue), icon: DollarSign },
            { l: 'Paid Orders', v: stats.total_orders, icon: Truck },
            { l: 'Products', v: stats.total_products, icon: Package },
            { l: 'Clients', v: stats.total_users, icon: Users },
            { l: 'Pending Orders', v: stats.pending_orders, icon: Clock },
            { l: 'Low Stock Alerts', v: stats.low_stock_count, icon: AlertTriangle },
          ].map((s) => (
            <div key={s.l} data-testid={`stat-${s.l.toLowerCase().replace(/ /g, '-')}`} className="border border-border bg-surface p-6">
              <s.icon size={18} className="text-gold mb-4" />
              <p className="font-serif text-3xl mb-1">{s.v}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">{s.l}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'products' && (
        <div>
          <button data-testid="admin-add-product-button" onClick={() => setEditing(null)} className="mb-6 flex items-center gap-2 bg-gold text-primary-foreground px-5 py-3 text-xs font-mono uppercase tracking-widest hover:bg-gold-hover"><Plus size={14} /> Add Product</button>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-${p.id}`} className="border border-border bg-surface p-4 flex gap-4">
                <img src={resolveImg(p.images?.[0])} alt="" className="w-16 h-20 object-cover bg-surface-elevated" />
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-serif text-lg">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.category} · {money(p.price)}</p>
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`edit-product-${p.id}`} onClick={() => setEditing(p)} className="text-muted-foreground hover:text-gold"><Pencil size={15} /></button>
                      <button data-testid={`delete-product-${p.id}`} onClick={() => delProduct(p.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {p.is_presale && <span className="text-[10px] font-mono px-2 py-0.5 bg-[#3B2A10] text-[#F3D082]">Presale</span>}
                    {p.featured && <span className="text-[10px] font-mono px-2 py-0.5 bg-gold/20 text-gold">Featured</span>}
                    <span className="text-[10px] font-mono px-2 py-0.5 bg-surface-elevated text-muted-foreground">Stock {p.stock}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'inventory' && (
        <div>
          {lowStock.length > 0 && (
            <div className="border border-[#7A571B] bg-[#3B2A10]/30 p-4 mb-6 flex items-center gap-3 text-sm text-[#F3D082]">
              <AlertTriangle size={16} /> {lowStock.length} product(s) at or below their low-stock threshold.
            </div>
          )}
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-muted-foreground uppercase tracking-wider border-b border-border">
              <th className="py-3">Product</th><th>Stock</th><th>Threshold</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {products.filter((p) => !p.is_presale).map((p) => (
                <tr key={p.id} data-testid={`inventory-row-${p.id}`} className="border-b border-border">
                  <td className="py-3 font-serif">{p.name}</td>
                  <td className="font-mono">{p.stock}</td>
                  <td className="font-mono text-muted-foreground">{p.low_stock_threshold}</td>
                  <td>{p.stock <= 0 ? <span className="text-destructive text-xs">Out of stock</span> : p.stock <= p.low_stock_threshold ? <span className="text-[#F3D082] text-xs">Low</span> : <span className="text-green-400 text-xs">In stock</span>}</td>
                  <td><button onClick={() => setEditing(p)} className="text-gold text-xs hover:underline">Adjust</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'presale' && (
        <div className="space-y-4">
          {presaleProducts.length === 0 && <p className="text-muted-foreground text-sm">No presale products in production.</p>}
          {presaleProducts.map((p) => (
            <div key={p.id} className="border border-border bg-surface p-5 flex items-center justify-between">
              <div>
                <p className="font-serif text-lg">{p.name}</p>
                <p className="text-xs text-muted-foreground">Dispatch {p.presale_dispatch_date || 'TBA'} · Limit {p.presale_limit || '∞'}</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-gold">{p.presale_reserved || 0} reserved</p>
                <button onClick={() => setEditing(p)} className="text-xs text-gold hover:underline mt-1">Manage</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'orders' && (
        <div className="space-y-4">
          {orders.length === 0 && <p className="text-muted-foreground text-sm">No orders yet.</p>}
          {orders.map((o) => (
            <div key={o.id} data-testid={`admin-order-${o.id}`} className="border border-border bg-surface p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="font-mono text-xs text-muted-foreground">#{o.id.slice(4, 14).toUpperCase()} · {o.user_email}</p>
                  <p className="text-sm mt-1">{o.items.map((i) => `${i.quantity}× ${i.name}`).join(', ')}</p>
                  <p className="text-xs text-muted-foreground mt-1">Deliver {o.delivery_date} · {o.delivery_slot} → {o.shipping?.city}, {o.shipping?.country}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-gold">{money(o.total, o.currency)}</p>
                  <p className="text-[10px] font-mono uppercase mt-1 text-muted-foreground">{o.payment_status}</p>
                </div>
              </div>
              <Select value={o.status} onValueChange={(v) => setOrderStatus(o.id, v)}>
                <SelectTrigger data-testid={`order-status-${o.id}`} className="w-48 bg-background border-border text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface-elevated border-border">
                  {['pending', 'confirmed', 'shipped', 'delivered'].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      )}

      {tab === 'users' && (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-muted-foreground uppercase tracking-wider border-b border-border"><th className="py-3">Name</th><th>Email</th><th>Provider</th><th>Role</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-b border-border">
                <td className="py-3 font-serif">{u.name}</td>
                <td className="text-muted-foreground">{u.email}</td>
                <td className="text-xs text-muted-foreground">{u.auth_provider}</td>
                <td>
                  <Select value={u.role} onValueChange={(v) => setRole(u.user_id, v)}>
                    <SelectTrigger className="w-32 bg-background border-border text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface-elevated border-border"><SelectItem value="customer">customer</SelectItem><SelectItem value="admin">admin</SelectItem></SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {tab === 'videos' && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-xs text-muted-foreground max-w-md">Add films by upload, YouTube ID, or paste @_ke_sa Instagram reel links. Connect Instagram to auto-sync.</p>
            <button data-testid="sync-instagram-button" onClick={syncInstagram} className="flex items-center gap-2 border border-gold/50 text-gold px-4 py-2 text-xs font-mono uppercase tracking-wider hover:bg-gold/10"><Instagram size={14} /> Sync from Instagram</button>
          </div>
          <div className="border border-border bg-surface p-6 mb-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label className="text-xs">Film Title</Label><Input data-testid="video-title-input" value={videoForm.title} onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })} className="mt-1 bg-background border-border" /></div>
            <div><Label className="text-xs">YouTube ID</Label><Input value={videoForm.youtube_id} onChange={(e) => setVideoForm({ ...videoForm, youtube_id: e.target.value })} className="mt-1 bg-background border-border" /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Instagram Reel URL (@_ke_sa)</Label><Input data-testid="video-instagram-input" value={videoForm.instagram_url} onChange={(e) => setVideoForm({ ...videoForm, instagram_url: e.target.value })} placeholder="https://www.instagram.com/reel/XXXXXXXXX/" className="mt-1 bg-background border-border" /></div>
            <div className="flex items-end gap-2"><UploadField label="Upload Video File" accept="video/*" onUploaded={(url) => setVideoForm({ ...videoForm, video_url: url })} />{videoForm.video_url && <span className="text-xs text-gold">✓ uploaded</span>}</div>
            <div className="sm:col-span-2"><Label className="text-xs">Linked Product</Label>
              <Select value={videoForm.product_id} onValueChange={(v) => setVideoForm({ ...videoForm, product_id: v })}>
                <SelectTrigger className="mt-1 bg-background border-border"><SelectValue placeholder="Select product (optional)" /></SelectTrigger>
                <SelectContent className="bg-surface-elevated border-border">{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <button data-testid="add-video-button" onClick={addVideo} className="sm:col-span-2 bg-gold text-primary-foreground py-3 text-xs font-mono uppercase tracking-widest hover:bg-gold-hover">Add Film</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {videos.map((v) => (
              <div key={v.id} className="border border-border bg-surface p-4 flex items-center justify-between">
                <div><p className="font-serif">{v.title}</p><p className="text-xs text-muted-foreground">{v.likes} likes</p></div>
                <button onClick={() => delVideo(v.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'bundles' && (
        <div>
          <div className="border border-border bg-surface p-6 mb-8 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input data-testid="bundle-name-input" placeholder="Set name" value={bundleForm.name} onChange={(e) => setBundleForm({ ...bundleForm, name: e.target.value })} className="bg-background border-border" />
              <Input data-testid="bundle-price-input" type="number" placeholder="Set price (USD)" value={bundleForm.price} onChange={(e) => setBundleForm({ ...bundleForm, price: e.target.value })} className="bg-background border-border" />
              <UploadField label="Set Image (optional)" accept="image/*" onUploaded={(url) => setBundleForm({ ...bundleForm, image: url })} />
            </div>
            <Textarea placeholder="Description" value={bundleForm.description} onChange={(e) => setBundleForm({ ...bundleForm, description: e.target.value })} className="bg-background border-border" />
            <div>
              <p className="text-xs text-muted-foreground mb-2">Select products in this set ({bundleForm.product_ids.length})</p>
              <div className="max-h-48 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-2">
                {products.map((p) => (
                  <button key={p.id} data-testid={`bundle-pick-${p.id}`} onClick={() => toggleBundleProduct(p.id)} className={`text-left text-xs border px-3 py-2 ${bundleForm.product_ids.includes(p.id) ? 'border-gold text-gold' : 'border-border text-muted-foreground'}`}>{p.name}</button>
                ))}
              </div>
            </div>
            <button data-testid="save-bundle-button" onClick={saveBundle} className="bg-gold text-primary-foreground px-6 py-3 text-xs font-mono uppercase tracking-widest hover:bg-gold-hover">Create Set</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bundles.map((b) => (
              <div key={b.id} data-testid={`admin-bundle-${b.id}`} className="border border-border bg-surface p-4 flex items-center justify-between">
                <div><p className="font-serif text-lg">{b.name}</p><p className="text-xs text-muted-foreground">{money(b.price)} · {b.products?.map((p) => p.name).join(', ')}</p></div>
                <button data-testid={`delete-bundle-${b.id}`} onClick={() => delBundle(b.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {editing !== undefined && <ProductForm product={editing} onClose={() => setEditing(undefined)} onSaved={loadAll} />}
    </div>
  );
}
